import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Checkbox, FileInput, TextArea } from 'common-src/components/base';
import { cachePortalThreadMessages, cachePortalThreads } from 'common-src/features/cache';
import { apiRequest, getRestErrorMessage, HttpMethods } from 'common-src/features/rest';
import { getPromptTemplatesRequest } from 'common-src/models/PromptTemplate';
import { getUniqueClientPromptTemplates } from 'common-src/models/PromptTemplate/selectors';
import { ColorsNew } from 'common-src/styles';

import useCustomSelector from 'src/hooks/useCustomSelector';
import usePopup from 'src/hooks/usePopup';
import BasePopup from 'src/popups/BasePopup';

import { NotificationTypeEnum, ReceiverTypeEnum } from './constants';
import styles from './SendSecureMessagePopup.module.scss';

const MAX_FILE_SIZE = 10 * 1024 * 1024;

const getNotificationType = ({ email, sms }) => {
  if (email && sms) return NotificationTypeEnum.BOTH;
  if (sms) return NotificationTypeEnum.SMS;
  return NotificationTypeEnum.EMAIL;
};

const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const { result } = reader;
      if (typeof result !== 'string') {
        reject(new Error('Failed to read attachment'));
        return;
      }

      const [, base64 = ''] = result.split(',');
      resolve(base64);
    };
    reader.onerror = () => reject(new Error('Failed to read attachment'));
    reader.readAsDataURL(file);
  });

const initialState = {
  subject: '',
  body: '',
  attachment: null,
  recipients: {
    patient: true,
    caregiver: false,
  },
  sendVia: {
    email: true,
    sms: false,
  },
};

const getNormalizedThreads = (items) =>
  items.map((item) => {
    const recipients = [];
    if (item.sendToPatient ?? item.send_to_patient) recipients.push('Patient');
    if (item.sendToCaregiver ?? item.send_to_caregiver) recipients.push('Caregiver');

    const createdAt = item.sentAt ?? item.sent_at ?? item.createdAt ?? item.created_at;
    const sentAtValue =
      createdAt && new Date(createdAt).getTime() > 0 ? new Date(createdAt).getTime() : 0;

    return {
      threadId: item.threadId ?? item.thread_id ?? item.id,
      subject: item.messageSubject ?? item.message_subject ?? '-',
      body: item.body ?? '-',
      recipients: recipients.join(', ') || '-',
      sentAt:
        sentAtValue > 0
          ? new Date(createdAt).toLocaleDateString('en-US', {
              month: '2-digit',
              day: '2-digit',
              year: 'numeric',
            })
          : '-',
      sentAtValue,
    };
  });

const getNormalizedThreadMessages = (items) =>
  items
    .map((item) => {
      const createdAt = item.sentAt ?? item.sent_at ?? item.createdAt ?? item.created_at;
      const sentAtValue =
        createdAt && new Date(createdAt).getTime() > 0 ? new Date(createdAt).getTime() : 0;
      return {
        id: item.id,
        body: item.body ?? '-',
        senderType: item.senderType ?? item.sender_type ?? '',
        attachmentId: item.attachmentId ?? item.attachment_id ?? null,
        sentAt:
          sentAtValue > 0
            ? new Date(createdAt).toLocaleString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
              })
            : '-',
        sentAtValue,
      };
    })
    .sort((a, b) => a.sentAtValue - b.sentAtValue);

const SendSecureMessagePopup = ({ patientId, onClose }) => {
  const dispatch = useDispatch();
  const [data, setData] = useState(initialState);
  const promptTemplates = useCustomSelector((state) => getUniqueClientPromptTemplates(state));

  useEffect(() => {
    dispatch(getPromptTemplatesRequest());
  }, [dispatch]);

  const { errors, setErrors, renderButtons } = usePopup();

  const hasRecipientSelected = useMemo(
    () => data.recipients.patient || data.recipients.caregiver,
    [data.recipients.patient, data.recipients.caregiver],
  );
  const hasSendViaSelected = useMemo(
    () => data.sendVia.email || data.sendVia.sms,
    [data.sendVia.email, data.sendVia.sms],
  );

  const setRecipient = (key, value) => {
    setData((prev) => ({
      ...prev,
      recipients: { ...prev.recipients, [key]: value },
    }));

    const updatedRecipients = { ...data.recipients, [key]: value };
    setErrors((prev) => ({
      ...prev,
      recipients:
        updatedRecipients.patient || updatedRecipients.caregiver
          ? ''
          : 'Select at least one recipient',
    }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setData((prev) => ({ ...prev, attachment: null }));
      setErrors((prev) => ({ ...prev, attachment: '' }));
      return;
    }

    setData((prev) => ({ ...prev, attachment: file }));
    setErrors((prev) => ({
      ...prev,
      attachment: file.size > MAX_FILE_SIZE ? 'File size must be less than 10MB' : '',
    }));
  };

  const setSendVia = (key, value) => {
    setData((prev) => ({
      ...prev,
      sendVia: { ...prev.sendVia, [key]: value },
    }));

    const updatedSendVia = { ...data.sendVia, [key]: value };
    setErrors((prev) => ({
      ...prev,
      sendVia: updatedSendVia.email || updatedSendVia.sms ? '' : 'Select at least one notify type',
    }));
  };

  const onSubmit = async (loadingCallback, successCallback, errorCallback) => {
    if (!patientId) {
      errorCallback('Patient is required before sending a secure message.');
      return;
    }

    if (!hasRecipientSelected) {
      setErrors((prev) => ({ ...prev, recipients: 'Select at least one recipient' }));
      return;
    }
    if (!hasSendViaSelected) {
      setErrors((prev) => ({ ...prev, sendVia: 'Select at least one notify type' }));
      return;
    }

    loadingCallback();

    try {
      let attachmentData = {};
      if (data.attachment) {
        const fileContent = await readFileAsBase64(data.attachment);
        attachmentData = {
          fileContent,
          fileName: data.attachment.name,
          fileType: data.attachment.type || 'application/octet-stream',
        };
      }

      const response = await apiRequest({
        endpoint: 'patientPortalMessages',
        method: HttpMethods.Post,
        body: {
          patientId,
          messageSubject: data.subject.trim(),
          body: data.body.trim(),
          sendToPatient: data.recipients.patient,
          sendToCaregiver: data.recipients.caregiver,
          receiverType: ReceiverTypeEnum.PATIENT,
          notificationType: getNotificationType(data.sendVia),
          ...attachmentData,
        },
      });

      if (!response.ok) {
        const err = await response.json();
        errorCallback(getRestErrorMessage(err) || 'Failed to send message');
        return;
      }

      const refreshResponse = await apiRequest({
        endpoint: `patientPortalMessages/patient/${patientId}/threads`,
      });
      if (refreshResponse.ok) {
        const refreshData = await refreshResponse.json();
        const payload =
          refreshData?.PatientPortalMessage ??
          refreshData?.patientPortalMessage ??
          refreshData?.patient_portal_message ??
          [];
        const items = Array.isArray(payload) ? payload : payload ? [payload] : [];
        dispatch(cachePortalThreads(patientId, getNormalizedThreads(items)));
      }

      const createdMessage = await response.json();
      const messagePayload =
        createdMessage?.PatientPortalMessage ??
        createdMessage?.patientPortalMessage ??
        createdMessage?.patient_portal_message ??
        null;
      const sentThreadId = messagePayload?.threadId ?? messagePayload?.thread_id ?? null;
      if (sentThreadId) {
        const threadResponse = await apiRequest({
          endpoint: `patientPortalMessages/thread/${sentThreadId}`,
        });
        if (threadResponse.ok) {
          const threadData = await threadResponse.json();
          const threadPayload =
            threadData?.PatientPortalMessage ??
            threadData?.patientPortalMessage ??
            threadData?.patient_portal_message ??
            [];
          const threadItems = Array.isArray(threadPayload)
            ? threadPayload
            : threadPayload
              ? [threadPayload]
              : [];
          dispatch(
            cachePortalThreadMessages(sentThreadId, getNormalizedThreadMessages(threadItems)),
          );
        }
      }

      successCallback('Message sent successfully!');
      onClose(true);
    } catch (error) {
      errorCallback(error?.message || 'Failed to send message');
    }
  };

  return (
    <BasePopup id="send-secure-message" open onClose={onClose} title="Send  Message">
      <div className={styles.content}>
        <TextArea
          id="secure-message-subject"
          type="template"
          label="Subject"
          placeholder="Enter subject"
          value={data.subject}
          onTextChange={(value) => setData((prev) => ({ ...prev, subject: value }))}
          templates={promptTemplates}
          maxRows={2}
          fixedRows={1}
          required
          requiredColor={ColorsNew.mediumDarkRed}
        />
        <TextArea
          id="secure-message-body"
          type="template"
          label="Body"
          placeholder="Enter message"
          value={data.body}
          onTextChange={(value) => setData((prev) => ({ ...prev, body: value }))}
          templates={promptTemplates}
          fixedRows={5}
          required
        />
        <p className={[styles.label, 'font-w-500', 'font-s-14'].join(' ')}>Attachment</p>
        <FileInput
          id="secure-message-attachment"
          inputAccept="*"
          placeholder="Click to select attachment"
          description="Optional, max 10MB"
          value={data.attachment}
          errorText={errors.attachment}
          handleFileChange={handleFileChange}
        />

        <div className={styles.recipients}>
          <p className={[styles.label, 'font-w-500', 'font-s-14'].join(' ')}>
            Recipients<span className={styles.requiredStar}>*</span>
          </p>
          <div className={['flex-row', 'gap-20'].join(' ')}>
            <Checkbox
              id="recipient-patient"
              label="Patient"
              checked={data.recipients.patient}
              onChange={(value) => setRecipient('patient', value)}
            />
            <Checkbox
              id="recipient-caregiver"
              label="Caregiver"
              checked={data.recipients.caregiver}
              onChange={(value) => setRecipient('caregiver', value)}
            />
          </div>
          <p className={[styles.error, 'font-s-12'].join(' ')}>{errors.recipients}</p>
        </div>
        <div className={styles.recipients}>
          <p className={[styles.label, 'font-w-500', 'font-s-14'].join(' ')}>
            Notify via<span className={styles.requiredStar}>*</span>
          </p>
          <div className={['flex-row', 'gap-20'].join(' ')}>
            <Checkbox
              id="send-via-email"
              label="Email"
              checked={data.sendVia.email}
              onChange={(value) => setSendVia('email', value)}
            />
            <Checkbox
              id="send-via-sms"
              label="SMS"
              checked={data.sendVia.sms}
              onChange={(value) => setSendVia('sms', value)}
              disabled
            />
          </div>
          <p className={[styles.error, 'font-s-12'].join(' ')}>{errors.sendVia}</p>
        </div>
      </div>
      {renderButtons({
        containerClassName: styles.buttonsContainer,
        onClose,
        onSubmit,
        isSubmitEnabled:
          !!data.subject.trim() &&
          !!data.body.trim() &&
          hasRecipientSelected &&
          hasSendViaSelected &&
          !errors.sendVia &&
          !errors.attachment,
        submitButtonText: 'Send',
      })}
    </BasePopup>
  );
};

SendSecureMessagePopup.propTypes = {
  patientId: PropTypes.number,
  onClose: PropTypes.func,
};

export default SendSecureMessagePopup;
